import { createFileRoute } from '@tanstack/react-router';
import type { InteractiveElement } from '@/scenes/Room/atom';
import { useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { diagramCommandAtom } from '@/scenes/Room/atom';
import { X, RotateCcw } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { ELEMENT_REGISTRY, CelebrationBurst, md, preprocessLatex } from '@/scenes/Room/InteractiveElements';
import { RoomThemeProvider } from '@/scenes/Room/RoomTheme';
import { SpokenPhraseReplay } from '@/scenes/Room/SpokenPhrase';
import type { SessionTheme } from '@/api';

export const Route = createFileRoute('/dev/interactive-preview')({
  component: InteractivePreview,
});

// ─── Mock Data ────────────────────────────────────────

const MOCKS: InteractiveElement[] = [
  { id: 'wb-1', type: 'whiteboard', title: 'Design a URL shortener', description: 'Draw the high-level design: clients, services, storage, cache.' },
  {
    id: 'mc-1', type: 'multiple_choice', title: 'What is the capital of France?', description: 'Test your geography knowledge.',
    options: [{ id: 'a', label: 'London', is_correct: false }, { id: 'b', label: 'Paris', is_correct: true }, { id: 'c', label: 'Berlin', is_correct: false }, { id: 'd', label: 'Madrid', is_correct: false }],
  },
  {
    id: 'pick-1', type: 'word_pick', title: 'Find the noun', description: "In 'Elle chante une chanson.', which word is the noun?",
    sentence: 'Elle chante une chanson.', correct_words: ['chanson'],
  },
  {
    id: 'quick-1', type: 'quick_choice', title: 'Pick the article', description: '___ pomme (an apple)',
    options: [{ id: 'un', label: 'un', is_correct: false }, { id: 'une', label: 'une', is_correct: true }, { id: 'des', label: 'des', is_correct: false }],
  },
  { id: 'text-1', type: 'text_input', title: 'Describe what you learned', description: 'In your own words, summarize the key takeaway.', placeholder: 'Type your reflection here...' },
  { id: 'img-1', type: 'image', title: 'The Solar System', description: 'Our solar system consists of eight planets orbiting the Sun.', image_url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c3/Solar_sys8.jpg/800px-Solar_sys8.jpg' },
  { id: 'rating-1', type: 'rating', title: 'How confident are you?', description: 'Rate your confidence in the material.', min_value: 1, max_value: 5 },
  {
    id: 'info-1', type: 'info_card', title: 'Key Takeaways', description: "Most important points from today's session.",
    bullets: ['Neural networks mimic biological neurons', 'Deep learning needs large datasets', 'Transfer learning saves training time'],
  },
  { id: 'highlight-1', type: 'highlight', title: 'Session Progress', label: 'Questions Answered', value: '12 / 15', description: "You're doing great — 3 more to go." },
  {
    id: 'checklist-1', type: 'checklist', title: 'Review Checklist',
    items: [{ id: 'c1', label: 'Variables and data types' }, { id: 'c2', label: 'Control flow (if/else, loops)' }, { id: 'c3', label: 'Functions and scope' }],
  },
  { id: 'fill-1', type: 'fill_blank', title: 'Complete the Sentence', sentence: 'The process of converting source code into machine code is called ___.' },
  {
    id: 'md-1', type: 'markdown', title: 'Quadratic Formula',
    content: '## The Quadratic Formula\n\nFor any quadratic equation $ax^2 + bx + c = 0$, the solutions are:\n\n$$x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$$\n\nWhere:\n- $a$ is the coefficient of $x^2$\n- $b$ is the coefficient of $x$\n- $c$ is the constant term\n\nThe **discriminant** $\\Delta = b^2 - 4ac$ determines the nature of the roots.',
  },
  {
    id: 'md-2', type: 'markdown', title: 'Code Example',
    content: '### Binary Search\n\n```python\ndef binary_search(arr, target):\n    lo, hi = 0, len(arr) - 1\n    while lo <= hi:\n        mid = (lo + hi) // 2\n        if arr[mid] == target:\n            return mid\n        elif arr[mid] < target:\n            lo = mid + 1\n        else:\n            hi = mid - 1\n    return -1\n```\n\nTime complexity: $O(\\log n)$\nSpace complexity: $O(1)$',
  },
  {
    id: 'code-1', type: 'code_challenge', title: 'What does this function return?',
    description: 'Given the function below, what is the output of calling mystery([3, 1, 4, 1, 5])?',
    code: 'def mystery(nums):\n    result = []\n    seen = set()\n    for n in nums:\n        if n not in seen:\n            result.append(n)\n            seen.add(n)\n    return result\n\nprint(mystery([3, 1, 4, 1, 5]))',
    code_language: 'python',
    placeholder: 'What gets printed?',
  },
  {
    id: 'code-2', type: 'code_challenge', title: 'Fix the Bug',
    description: 'This JavaScript function should return the sum of all elements, but it has a bug. What is wrong?',
    code: 'function sumArray(arr) {\n  let sum = 1;\n  for (let i = 0; i < arr.length; i++) {\n    sum += arr[i];\n  }\n  return sum;\n}',
    code_language: 'javascript',
    placeholder: 'Describe the bug and fix...',
  },
  {
    id: 'code-3', type: 'code_challenge', title: 'SQL Challenge',
    description: 'Write a query to find all users who registered in the last 30 days and have made at least one purchase.',
    code: 'CREATE TABLE users (\n  id INT PRIMARY KEY,\n  name VARCHAR(100),\n  registered_at TIMESTAMP\n);\n\nCREATE TABLE purchases (\n  id INT PRIMARY KEY,\n  user_id INT REFERENCES users(id),\n  amount DECIMAL(10,2),\n  purchased_at TIMESTAMP\n);',
    code_language: 'sql',
    placeholder: 'Write your SQL query...',
  },
  {
    id: 'speak-1', type: 'speak_practice', title: 'Say this in French',
    target_text: 'Bonjour, comment allez-vous aujourd\'hui?',
    language_label: 'French',
    description: 'Try to pronounce each word clearly.',
  },
  {
    id: 'match-1', type: 'word_match', title: 'Match the French words',
    description: 'Tap a word on the left, then its translation on the right.',
    pairs: [
      { id: 'p1', left: 'Bonjour', right: 'Hello' },
      { id: 'p2', left: 'Merci', right: 'Thank you' },
      { id: 'p3', left: 'Chat', right: 'Cat' },
      { id: 'p4', left: 'Maison', right: 'House' },
      { id: 'p5', left: 'Livre', right: 'Book' },
    ],
  },
  {
    id: 'match-2', type: 'word_match', title: 'Match the programming concepts',
    pairs: [
      { id: 'p1', left: 'Array', right: 'Ordered collection' },
      { id: 'p2', left: 'HashMap', right: 'Key-value store' },
      { id: 'p3', left: 'Stack', right: 'LIFO structure' },
      { id: 'p4', left: 'Queue', right: 'FIFO structure' },
    ],
  },
  {
    id: 'ab-1', type: 'ab_compare', title: 'Which response is better?',
    description: 'Both responses answer "Explain quantum computing simply." Pick the better one.',
    option_a: {
      label: 'Response A',
      content: 'Quantum computing leverages quantum mechanical phenomena such as superposition and entanglement to perform computations. Unlike classical bits which exist in states of 0 or 1, qubits can exist in multiple states simultaneously, enabling parallel processing of vast computational spaces.',
    },
    option_b: {
      label: 'Response B',
      content: "Imagine a coin spinning in the air — it's both heads and tails at once. That's a qubit. Regular computers flip coins one at a time. Quantum computers spin millions of coins simultaneously, solving in seconds what would take years.",
    },
    correct_option: 'b' as const,
    explanation: 'Response B uses a concrete analogy (spinning coin) that makes the abstract concept tangible. Response A is accurate but reads like a textbook — it tells rather than teaches. Great prompts ask for analogies and specify the audience level.',
  },
  {
    id: 'sort-1', type: 'drag_sort', title: 'Order the prompt components',
    description: 'Arrange these from first to last in an effective system prompt.',
    sort_items: [
      { id: 'role', label: '🎭 Role — "You are a senior data analyst"' },
      { id: 'context', label: '📋 Context — background info & constraints' },
      { id: 'task', label: '🎯 Task — the specific thing to do' },
      { id: 'rules', label: '⚠️ Rules — what to avoid or enforce' },
      { id: 'format', label: '📐 Format — how to structure the output' },
    ],
    correct_order: ['role', 'context', 'task', 'rules', 'format'],
  },
  {
    id: 'swipe-1', type: 'swipe_cards', title: 'Good prompt or bad prompt?',
    description: 'Swipe right for good prompts, left for bad ones.',
    left_label: 'Bad Prompt',
    right_label: 'Good Prompt',
    swipe_cards: [
      { id: 's1', content: '"Write me something about dogs"', correct_direction: 'left' as const, explanation: 'Too vague — no audience, format, or purpose specified.' },
      { id: 's2', content: '"Act as a vet. Explain 3 common puppy vaccinations to a first-time dog owner. Use simple language, one paragraph each."', correct_direction: 'right' as const, explanation: 'Clear role, audience, format, and constraints.' },
      { id: 's3', content: '"Make it better"', correct_direction: 'left' as const, explanation: 'No context about what "it" is or what "better" means.' },
      { id: 's4', content: '"Rewrite this email to be 50% shorter. Keep the call-to-action and deadline. Match a professional but warm tone."', correct_direction: 'right' as const, explanation: 'Specific, measurable, with clear constraints.' },
      { id: 's5', content: '"You are the world\'s best writer. Write the most amazing blog post ever about AI."', correct_direction: 'left' as const, explanation: 'Superlatives don\'t help. No specifics on audience, length, angle, or style.' },
    ],
  },
  {
    id: 'reveal-1', type: 'reveal_sequence', title: 'What happens when you hit Send?',
    description: 'Tap each step to discover how an LLM processes your prompt.',
    steps: [
      { id: 'r1', title: 'Tokenization', content: 'Your text is split into tokens — subword pieces like "un", "believ", "able". GPT-4 uses ~100K token vocabulary. "ChatGPT is great" = 4 tokens.' },
      { id: 'r2', title: 'Embedding', content: 'Each token becomes a 1,536-dimensional vector — a point in space where "king" minus "man" plus "woman" ≈ "queen". Meaning is geometry.' },
      { id: 'r3', title: 'Attention', content: '96 attention layers ask: "For each word, which other words matter most?" This is where the model actually "reads" — relating every token to every other token.' },
      { id: 'r4', title: 'Generation', content: 'The model predicts probability of each possible next token. "The cat sat on the ___" → mat (31%), floor (18%), bed (12%). Temperature controls how adventurous the pick is.' },
      { id: 'r5', title: 'Decoding', content: 'One token is chosen and appended. The whole process repeats — now including the new token. This is why AI "writes" one word at a time, left to right, with no plan for the ending.' },
    ],
  },
  {
    id: 'poll-1', type: 'poll', title: 'What matters most in a prompt?',
    description: 'Vote, then see how 2,847 other learners answered.',
    poll_options: [
      { id: 'p1', label: 'Being specific about the task', percentage: 42 },
      { id: 'p2', label: 'Setting the right role/persona', percentage: 18 },
      { id: 'p3', label: 'Providing examples (few-shot)', percentage: 27 },
      { id: 'p4', label: 'Specifying the output format', percentage: 13 },
    ],
    total_responses: 2847,
    insight: 'Surprise: "providing examples" outperforms "setting a role" by 50%. Few-shot prompting is the single most underused technique by beginners.',
  },
  {
    id: 'html-canvas-1', type: 'html_canvas' as const, title: 'Binary Tree Visualization',
    description: 'A self-contained HTML visualization rendered in a sandboxed iframe.',
    html_content: `<!DOCTYPE html><html><head><style>*{margin:0;padding:0;box-sizing:border-box}body{background:#0a0a0b;display:flex;align-items:center;justify-content:center;height:100vh;font-family:system-ui,sans-serif}</style></head><body><svg width="400" height="300" viewBox="0 0 400 300"><line x1="200" y1="50" x2="100" y2="130" stroke="#444" stroke-width="2"/><line x1="200" y1="50" x2="300" y2="130" stroke="#444" stroke-width="2"/><line x1="100" y1="130" x2="60" y2="210" stroke="#444" stroke-width="2"/><line x1="100" y1="130" x2="140" y2="210" stroke="#444" stroke-width="2"/><line x1="300" y1="130" x2="260" y2="210" stroke="#444" stroke-width="2"/><line x1="300" y1="130" x2="340" y2="210" stroke="#444" stroke-width="2"/><circle cx="200" cy="50" r="24" fill="#1e1e22" stroke="#8b5cf6" stroke-width="2"/><text x="200" y="55" fill="#fff" font-size="14" text-anchor="middle" font-weight="600">8</text><circle cx="100" cy="130" r="24" fill="#1e1e22" stroke="#3b82f6" stroke-width="2"/><text x="100" y="135" fill="#fff" font-size="14" text-anchor="middle" font-weight="600">3</text><circle cx="300" cy="130" r="24" fill="#1e1e22" stroke="#3b82f6" stroke-width="2"/><text x="300" y="135" fill="#fff" font-size="14" text-anchor="middle" font-weight="600">10</text><circle cx="60" cy="210" r="24" fill="#1e1e22" stroke="#10b981" stroke-width="2"/><text x="60" y="215" fill="#fff" font-size="14" text-anchor="middle" font-weight="600">1</text><circle cx="140" cy="210" r="24" fill="#1e1e22" stroke="#10b981" stroke-width="2"/><text x="140" y="215" fill="#fff" font-size="14" text-anchor="middle" font-weight="600">6</text><circle cx="260" cy="210" r="24" fill="#1e1e22" stroke="#10b981" stroke-width="2"/><text x="260" y="215" fill="#fff" font-size="14" text-anchor="middle" font-weight="600">9</text><circle cx="340" cy="210" r="24" fill="#1e1e22" stroke="#10b981" stroke-width="2"/><text x="340" y="215" fill="#fff" font-size="14" text-anchor="middle" font-weight="600">14</text></svg></body></html>`,
  },
  {
    id: 'diagram-1', type: 'interactive_diagram' as const, title: 'Liquid rocket engine',
    description: 'Labeled diagram the tutor drives (highlight / state / tap-a-part).',
    parts: [{"id": "propellant-feed", "label": "Propellant Feed Lines"}, {"id": "injector-plate", "label": "Injector Plate"}, {"id": "combustion-chamber", "label": "Combustion Chamber"}, {"id": "throat", "label": "Throat"}, {"id": "nozzle-bell", "label": "Diverging Nozzle Bell"}, {"id": "exhaust", "label": "Exhaust Plume"}],
    states: [{ name: 'firing', label: 'Engine firing' }],
    svg: "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 1000 600\">\n  <style>\n    .bg { fill: transparent; }\n    .stroke-light { stroke: #e5e7eb; stroke-width: 2; fill: none; }\n    .fill-dark { fill: #1f2937; }\n    .fill-accent { fill: #3b82f6; opacity: 0.2; }\n    .fill-accent-red { fill: #ef4444; opacity: 0.2; }\n    .text-label { fill: #e5e7eb; font-family: sans-serif; font-size: 24px; }\n    .plume { opacity: 0; fill: #fb923c; }\n    .chamber-fire { opacity: 0; fill: #fca5a5; }\n    svg.state-firing .plume { opacity: 0.8; animation: flicker 0.1s infinite alternate; }\n    svg.state-firing .chamber-fire { opacity: 0.6; animation: flicker 0.15s infinite alternate; }\n    @keyframes flicker {\n      0% { transform: scaleY(0.98); opacity: 0.7; }\n      100% { transform: scaleY(1.02); opacity: 0.9; }\n    }\n  </style>\n\n  <!-- Engine Wall (Continuous Contour) -->\n  <path d=\"M 420 150 L 420 250 C 420 300, 460 320, 460 350 C 460 400, 380 480, 380 500 L 360 500 C 360 480, 440 400, 440 350 C 440 320, 400 300, 400 250 L 400 150 Z\" class=\"stroke-light fill-dark\" />\n  <path d=\"M 580 150 L 580 250 C 580 300, 540 320, 540 350 C 540 400, 620 480, 620 500 L 640 500 C 640 480, 560 400, 560 350 C 560 320, 600 300, 600 250 L 600 150 Z\" class=\"stroke-light fill-dark\" />\n\n  <!-- Firing State Animations -->\n  <g transform=\"translate(500, 500)\">\n    <path d=\"M -120 0 L 120 0 L 160 100 L -160 100 Z\" class=\"plume\" />\n  </g>\n  <path d=\"M 420 150 L 580 150 L 580 250 C 580 300, 540 320, 540 350 C 540 400, 620 480, 620 500 L 380 500 C 380 480, 460 400, 460 350 C 460 320, 420 300, 420 250 Z\" class=\"chamber-fire\" />\n\n  <!-- Parts -->\n  <g data-part=\"propellant-feed\">\n    <path d=\"M 440 50 L 440 130 M 460 50 L 460 130\" class=\"stroke-light fill-accent-red\" />\n    <path d=\"M 540 50 L 540 130 M 560 50 L 560 130\" class=\"stroke-light fill-accent\" />\n    <line x1=\"280\" y1=\"90\" x2=\"440\" y2=\"90\" class=\"stroke-light\" />\n    <text x=\"270\" y=\"95\" text-anchor=\"end\" class=\"text-label\">Propellant Feed Lines</text>\n  </g>\n\n  <g data-part=\"injector-plate\">\n    <rect x=\"420\" y=\"130\" width=\"160\" height=\"20\" class=\"stroke-light fill-dark\" />\n    <line x1=\"720\" y1=\"140\" x2=\"580\" y2=\"140\" class=\"stroke-light\" />\n    <text x=\"730\" y=\"145\" text-anchor=\"start\" class=\"text-label\">Injector Plate</text>\n  </g>\n\n  <g data-part=\"combustion-chamber\">\n    <line x1=\"280\" y1=\"200\" x2=\"420\" y2=\"200\" class=\"stroke-light\" />\n    <text x=\"270\" y=\"205\" text-anchor=\"end\" class=\"text-label\">Combustion Chamber</text>\n  </g>\n\n  <g data-part=\"throat\">\n    <line x1=\"280\" y1=\"350\" x2=\"460\" y2=\"350\" class=\"stroke-light\" />\n    <text x=\"270\" y=\"355\" text-anchor=\"end\" class=\"text-label\">Throat</text>\n  </g>\n\n  <g data-part=\"nozzle-bell\">\n    <line x1=\"720\" y1=\"430\" x2=\"600\" y2=\"430\" class=\"stroke-light\" />\n    <text x=\"730\" y=\"435\" text-anchor=\"start\" class=\"text-label\">Diverging Nozzle Bell</text>\n  </g>\n\n  <g data-part=\"exhaust-plume\">\n    <line x1=\"720\" y1=\"550\" x2=\"550\" y2=\"550\" class=\"stroke-light\" />\n    <text x=\"730\" y=\"555\" text-anchor=\"start\" class=\"text-label\">Exhaust Plume</text>\n  </g>\n</svg>",
  },
  {
    // ?audio=<recording url>&sheet=<question paper url> (e.g. signed URLs of a listening material); short timings here.
    id: 'listening-1', type: 'listening_exercise' as const, title: 'Part 1: Shipping insurance',
    description: 'Choose the correct letter, A, B or C.',
    audio_url: new URLSearchParams(window.location.search).get('audio') ?? '',
    question_sheet_url: new URLSearchParams(window.location.search).get('sheet') ?? '',
    reading_seconds: 20,
    checking_seconds: 15,
    listening_questions: [
      { number: 9, prompt: 'Type of insurance chosen', kind: 'choice', options: [{ id: 'A', label: 'Economy' }, { id: 'B', label: 'Standard' }, { id: 'C', label: 'Premium' }] },
      { number: 10, prompt: 'Customer wants goods delivered to', kind: 'choice', options: [{ id: 'A', label: 'port' }, { id: 'B', label: 'home' }, { id: 'C', label: 'depot' }] },
      { number: 11, prompt: 'Name: Jacob ___', kind: 'gap', options: [] },
    ],
  },
];

// ─── Themes ───────────────────────────────────────────

const THEMES: { name: string; theme: SessionTheme }[] = [
  { name: 'Indigo', theme: { scheme: 'dark', page: '#0d0f1f', card: '#171a33', text: '#eef0ff', accent: '#818cf8', accent_text: '#0d0f1f', source: 'topic' } },
  { name: 'Ocean', theme: { scheme: 'dark', page: '#06161d', card: '#0d2530', text: '#e6f7fb', accent: '#22d3ee', accent_text: '#06161d', source: 'topic' } },
  { name: 'Forest', theme: { scheme: 'dark', page: '#0a1610', card: '#12241a', text: '#ecf8ef', accent: '#4ade80', accent_text: '#0a1610', source: 'topic' } },
  { name: 'Ember', theme: { scheme: 'dark', page: '#1a0f0a', card: '#2a1a12', text: '#fff3ea', accent: '#fb923c', accent_text: '#1a0f0a', source: 'topic' } },
  { name: 'Plum', theme: { scheme: 'dark', page: '#170b1a', card: '#26142b', text: '#fbeffd', accent: '#e879f9', accent_text: '#170b1a', source: 'topic' } },
  { name: 'Paper', theme: { scheme: 'light', page: '#f4f1ea', card: '#fffdf8', text: '#27231c', accent: '#c2410c', accent_text: '#ffffff', source: 'topic' } },
  { name: 'Sky', theme: { scheme: 'light', page: '#eaf2fb', card: '#ffffff', text: '#0f2540', accent: '#2563eb', accent_text: '#ffffff', source: 'topic' } },
  { name: 'Mint', theme: { scheme: 'light', page: '#e9f6f0', card: '#ffffff', text: '#0f2b20', accent: '#059669', accent_text: '#ffffff', source: 'topic' } },
  { name: 'Mono', theme: { scheme: 'dark', page: '#000000', card: '#141414', text: '#ffffff', accent: '#ffffff', accent_text: '#000000', source: 'topic' } },
];

function ThemePicker({ value, onChange }: { value: string; onChange: (name: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {THEMES.map(({ name, theme }) => (
        <button key={name} onClick={() => onChange(name)} title={name}
          className={`flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${value === name ? 'bg-m-surface-active text-m-text ring-1 ring-m-accent' : 'bg-m-surface text-m-text-muted hover:text-m-text-secondary'}`}>
          <span className="relative h-5 w-5 rounded-full overflow-hidden flex-shrink-0" style={{ background: theme.card, boxShadow: `inset 0 0 0 1px ${theme.text}22` }}>
            <span className="absolute right-0 bottom-0 h-2.5 w-2.5 rounded-full" style={{ background: theme.accent }} />
          </span>
          {name}
        </button>
      ))}
    </div>
  );
}

// ─── Card wrapper ─────────────────────────────────────

const WIDE_TYPES = ['code_challenge', 'markdown', 'word_match', 'html_canvas', 'ab_compare', 'reveal_sequence', 'interactive_diagram', 'whiteboard'];
const NO_DESCRIPTION_TYPES = ['image', 'info_card', 'highlight', 'markdown', 'speak_practice', 'code_challenge'];

const widthClass = (type: InteractiveElement['type']) =>
  // The room shows a listening exercise at its 680px exam width.
  type === 'listening_exercise' ? 'max-w-[680px]' : WIDE_TYPES.includes(type) ? 'max-w-[520px]' : 'max-w-[380px]';

function ElementHeader({ element }: { element: InteractiveElement }) {
  if (element.type === 'speak_practice') {
    return <h3 className="flex-1 text-[13px] font-medium text-m-text leading-snug">{element.title}</h3>;
  }
  return (
    <div className="flex-1 min-w-0">
      <h3 className="text-[13px] font-medium text-m-text leading-snug">{element.title}</h3>
      {element.description && !NO_DESCRIPTION_TYPES.includes(element.type) && (
        <p className="text-[13px] text-m-text-secondary mt-1 leading-relaxed" dangerouslySetInnerHTML={{ __html: md.renderInline(preprocessLatex(element.description)) }} />
      )}
    </div>
  );
}

function Card({ element }: { element: InteractiveElement }) {
  const [key, setKey] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const R = ELEMENT_REGISTRY[element.type];
  return (
    <div className={`w-full rounded-3xl bg-m-card ring-1 ring-m-border-subtle shadow-sm overflow-hidden relative ${widthClass(element.type)}`}>
      {showCelebration && <CelebrationBurst onDone={() => setShowCelebration(false)} />}
      {element.type !== 'whiteboard' && <div className="px-4 pt-3.5 pb-0 flex items-start justify-between gap-2">
        <ElementHeader element={element} />
        <button onClick={() => setKey((k) => k + 1)} className="p-0.5 text-m-text-faint hover:text-m-text-muted transition-colors flex-shrink-0" title="Reset">
          <RotateCcw className="h-3 w-3" />
        </button>
      </div>}
      {element.type === 'interactive_diagram' && <DiagramControls element={element} />}
      <div className={element.type === 'whiteboard' ? 'p-2' : 'px-4 pb-4 pt-2'} key={key}>
        <R element={element} onSubmit={(v, id) => console.log('Response:', { type: element.type, v, id })} onCelebrate={() => setShowCelebration(true)} />
        {element.type === 'speak_practice' && element.target_text && (
          <div className="mt-3 pt-3 border-t border-m-border-subtle"><SpokenPhraseReplay text={element.target_text} /></div>
        )}
      </div>
    </div>
  );
}

/** Fires the same commands the UI observer sends through the diagram_command RPC. */
function DiagramControls({ element }: { element: InteractiveElement }) {
  const send = useSetAtom(diagramCommandAtom);
  const fire = (action: 'highlight' | 'state' | 'ask', extra: { part?: string; state?: string; prompt?: string }) =>
    send({ elementId: element.id, action, requestId: Date.now(), ...extra });
  return (
    <div className="px-4 pt-2 flex flex-wrap gap-1.5">
      {(element.parts || []).map((part) => (
        <button key={part.id} onClick={() => fire('highlight', { part: part.id })}
          className="px-2 py-1 text-[11px] rounded-lg bg-m-surface-hover text-m-text-secondary hover:bg-m-surface-active">{part.label}</button>
      ))}
      <button onClick={() => fire('state', { state: 'firing' })} className="px-2 py-1 text-[11px] rounded-lg bg-orange-500/20 text-orange-200">Fire</button>
      <button onClick={() => fire('state', { state: 'none' })} className="px-2 py-1 text-[11px] rounded-lg bg-m-surface-hover text-m-text-secondary">Stop</button>
      <button onClick={() => fire('ask', { prompt: 'Tap where the gas reaches the speed of sound' })}
        className="px-2 py-1 text-[11px] rounded-lg bg-sky-500/20 text-sky-200">Ask: tap the throat</button>
    </div>
  );
}

// ─── Overlay sim ──────────────────────────────────────

function OverlaySim({ element, onCelebrate }: { element: InteractiveElement | null; onCelebrate?: () => void }) {
  const R = element ? ELEMENT_REGISTRY[element.type] : null;
  return (
    <AnimatePresence mode="wait">
      {element && R && (
        <>
          <motion.div key={`bg-${element.id}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
            className="absolute inset-0 z-[10] bg-black/60 backdrop-blur-sm" />
          <motion.div key={element.id} initial={{ opacity: 0, y: 32, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0 z-[20] flex items-center justify-center p-4">
            <div className={`w-full rounded-3xl bg-m-card ring-1 ring-m-border-subtle backdrop-blur-xl shadow-2xl overflow-hidden ${widthClass(element.type)}`}>
              {element.type !== 'whiteboard' && <div className="px-4 pt-3.5 pb-0 flex items-start justify-between gap-2">
                <ElementHeader element={element} />
                <button className="p-0.5 text-m-text-faint hover:text-m-text-muted transition-colors flex-shrink-0"><X className="h-3 w-3" /></button>
              </div>}
              <div className={`${element.type === 'whiteboard' ? 'p-2' : 'px-4 pb-4 pt-2'} max-h-[70vh] overflow-y-auto`}>
                <R element={element} onSubmit={(v, id) => console.log('Overlay:', { v, id })} onCelebrate={onCelebrate} />
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ─── Page ─────────────────────────────────────────────

/** `?fixture=<name>` renders the elements a real session showed (saved to /dev-fixtures/<name>.json). */
function useFixtureElements(): InteractiveElement[] | null {
  const [elements, setElements] = useState<InteractiveElement[] | null>(null);
  useEffect(() => {
    const name = new URLSearchParams(window.location.search).get('fixture');
    if (!name) return;
    fetch(`/dev-fixtures/${name}.json`)
      .then((res) => res.json())
      .then((data: InteractiveElement[]) => setElements(data))
      .catch((err) => console.error('Failed to load fixture', err));
  }, []);
  return elements;
}

function InteractivePreview() {
  const fixture = useFixtureElements();
  const elements = fixture ?? MOCKS;
  const [overlay, setOverlay] = useState<InteractiveElement | null>(null);
  const [view, setView] = useState<'grid' | 'overlay'>('grid');
  const [showCelebration, setShowCelebration] = useState(false);
  const [themeName, setThemeName] = useState(() => new URLSearchParams(window.location.search).get('theme') ?? THEMES[0].name);
  const theme = THEMES.find((t) => t.name === themeName)?.theme ?? THEMES[0].theme;

  return (
    <RoomThemeProvider theme={theme}>
    <div className="min-h-screen bg-m-page text-m-text transition-colors">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Interactive Elements</h1>
            <p className="text-[13px] text-m-text-muted mt-0.5">Preview all {elements.length} element types. Responses logged to console.</p>
          </div>
          <div className="flex gap-0.5 bg-m-surface rounded-2xl p-0.5">
            <button onClick={() => setView('grid')} className={`px-3.5 py-1.5 text-[12px] font-medium rounded-xl transition-all ${view === 'grid' ? 'bg-m-surface-active text-m-text' : 'text-m-text-muted hover:text-m-text-secondary'}`}>Grid</button>
            <button onClick={() => setView('overlay')} className={`px-3.5 py-1.5 text-[12px] font-medium rounded-xl transition-all ${view === 'overlay' ? 'bg-m-surface-active text-m-text' : 'text-m-text-muted hover:text-m-text-secondary'}`}>Overlay</button>
          </div>
        </div>
        <div className="mb-6"><ThemePicker value={themeName} onChange={setThemeName} /></div>

        {view === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {elements.map((el) => (
              <div key={el.id} className="flex justify-center">
                <Card element={el} />
              </div>
            ))}
          </div>
        ) : (
          <div className="relative grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
            <div className="space-y-1.5 lg:max-h-[calc(100vh-140px)] lg:overflow-y-auto">
              {MOCKS.map((el) => (
                <button key={el.id} onClick={() => setOverlay(overlay?.type === el.type && overlay?.id?.startsWith(el.id) ? null : { ...el, id: `${el.id}-${Date.now()}` })}
                  className={`w-full text-left px-3 py-2.5 rounded-2xl transition-all ${overlay?.type === el.type ? 'bg-m-surface-active' : 'hover:bg-m-surface'}`}>
                  <p className="text-[10px] uppercase tracking-wider text-m-text-faint mb-0.5">{el.type.replace(/_/g, ' ')}</p>
                  <p className="text-[13px] text-m-text-secondary leading-snug truncate">{el.title}</p>
                </button>
              ))}
            </div>
            <div className="relative rounded-3xl bg-m-surface overflow-hidden min-h-[480px] lg:min-h-[calc(100vh-140px)]">
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-[13px] text-m-text-faint">Select an element</p>
              </div>
              {showCelebration && <CelebrationBurst onDone={() => setShowCelebration(false)} />}
              <OverlaySim element={overlay} onCelebrate={() => setShowCelebration(true)} />
            </div>
          </div>
        )}
      </div>
    </div>
    </RoomThemeProvider>
  );
}
